import { format, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { TimeframeConfig, TimeframeOption } from '../types';

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// Helper to calculate fresh dates on demand
const getTimeframeConfig = (value: TimeframeOption, label: string, days: number): TimeframeConfig => {
	const now = new Date();
	return {
		label,
		value,
		startDate: formatDate(subDays(now, days)),
		endDate: formatDate(now),
	};
};

export const useTimeframeOptions = (): TimeframeConfig[] => {
	const { t } = useTranslation('navigation');

	// Calculate fresh dates on each call - no memoization to avoid stale dates
	// if users keep the app open across midnight or for extended periods
	return [
		getTimeframeConfig('30_days', t('leaderboard.timeframe.30days'), 30),
		getTimeframeConfig('60_days', t('leaderboard.timeframe.60days'), 60),
		getTimeframeConfig('90_days', t('leaderboard.timeframe.90days'), 90),
		getTimeframeConfig('120_days', t('leaderboard.timeframe.120days'), 120),
		getTimeframeConfig('custom', t('leaderboard.timeframe.custom'), 30),
	];
};

export const useTimeframeDates = (timeframe: TimeframeOption, customStartDate?: string, customEndDate?: string): { startDate: string; endDate: string } => {
	const options = useTimeframeOptions();

	// For custom timeframe, use the provided custom dates
	if (timeframe === 'custom' && customStartDate && customEndDate) {
		return {
			startDate: customStartDate,
			endDate: customEndDate,
		};
	}

	const config = options.find((opt) => opt.value === timeframe);

	if (!config) {
		// Default to 30 days
		const now = new Date();
		return {
			startDate: formatDate(subDays(now, 30)),
			endDate: formatDate(now),
		};
	}

	return {
		startDate: config.startDate,
		endDate: config.endDate,
	};
};
